import React, { useEffect, useState } from "react";
import dayjs from "dayjs";

//Material UI
import Fab from '@mui/material/Fab';
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

// Icons
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';

//Components
import Filters from "./filters";
import { DataGrid } from '@arredo/reactjs-ui';

//Hooks
import useQueryString from "../../hooks/queryString/useQueryString";
import useUser from '../../hooks/user/useUser';
import useNotify from '../../hooks/notify';
import useStyles from "./styles";

// Service
import replacementUnitService from "../../services/replacementUnitService";

// Utils
import { utils } from '../../utils/utils';

const columns = [
  {
    field: 'refId',
    header: 'Código',
    sizes: { xs: 4 }
  },
  {
    field: 'shortDescription',
    header: 'Descripción',
    sizes: { xs: 4 }
  },
  {
    field: 'quantity',
    header: 'Cantidad',
    align: 'center',
    sizes: { xs: 2 }
  },
  {
    field: 'stock',
    header: 'Stock',
    align: 'center',
    sizes: { xs: 2 }
  }
]

const initialFilters = {
  pageSize: 10,
  pageNumber: 1,
  endDate: dayjs().format('YYYY-MM-DD'),
  startDate: dayjs().add(-7, 'day').format('YYYY-MM-DD'),
  startTime: '',
  endTime: '',
  shortDescription: '',
  refId: ''
}

const ReplacementUnits = () => {
  const { classes } = useStyles();
  const { branch } = useUser();
  const { Notify, variants } = useNotify();
  const { filters, setParameterSearch, clearFilters } = useQueryString(initialFilters);

  const [loading, setLoading] = useState(true);

  const [searchResult, setSearchResult] = useState({
    products: [],
    totalPage: 0,
    totalResults: 0
  })


  useEffect(() => {
    const abortController = new AbortController();

    const isDatesValid = ({ startDate, endDate }) => {
      if (!startDate || !endDate)
        return false;

      const startDateFormatted = dayjs(startDate)
      const endDateFormatted = dayjs(endDate)

      const diff = endDateFormatted.diff(startDateFormatted, 'days');
      return diff >= 0 && diff <= 7;
    }

    const search = () => {
      setLoading(true);
      replacementUnitService
        .GetReplacementUnits({
          ...filters,
          branchId: branch?.id,
          branchCode: branch?.code,
          businessId: branch?.businessId,
          warehouseId: branch?.warehouseId
        }, { signal: abortController.signal })
        .then((res) => {
          setSearchResult({
            products: res.values,
            totalPage: res.totalPage,
            totalResults: res.totalResults
          });
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            Notify('Ocurrió un problema al obtener las unidades vendidas.', variants.ERROR);
            setSearchResult({ products: [] });
          }
        })
        .finally(() => setLoading(false));
    }

    if (isDatesValid(filters) && branch?.id)
      search();
    else
      setSearchResult([]);

    return () => {
      abortController.abort();
    };
  }, [filters, branch, Notify, variants]);

  const handleChangePage = (e, page) =>
    setParameterSearch({ pageNumber: page });

  const handleClickExport = () => {
    setLoading(true);
    replacementUnitService
      .ExportReplacementUnits({
        ...filters,
        branchId: branch?.id,
        branchCode: branch?.code,
        businessId: branch?.businessId,
        warehouseId: branch?.warehouseId
      })
      .then(blob => utils.export(blob, 'Reposicion_de_venta'))
      .catch(() => Notify('Ocurrio un problema al intentar exportar.', variants.ERROR))
      .finally(() => setLoading(false));
  }


  const handleClear = () => {
    clearFilters();
    setParameterSearch(initialFilters);
  }

  return (
    <>
      <Grid container direction="column" spacing={1}>
        <Grid>
          <Paper className={classes.paper}>
            <Typography variant="h4">Reposición de Venta</Typography>
          </Paper>
        </Grid>

        <Grid>
          <Filters
            filters={filters}
            setFilters={setParameterSearch}
            clearFilters={handleClear}
          />
        </Grid>

        <Grid>
          <DataGrid
            columns={columns}
            rows={searchResult.products}
            loading={loading}
            paginationProps={{
              count: Number(searchResult.totalPage || 0),
              page: Number(filters?.pageNumber || 1),
              onChange: handleChangePage
            }}
          />
        </Grid>
      </Grid>
      <div className={classes.fabButtons}>
        <Fab
          color='primary'
          variant="extended"
          className={classes.fab}
          onClick={handleClickExport}
          disabled={loading || searchResult.totalResults <= 0}
        >
          <VerticalAlignBottomIcon /> Exportar ({searchResult.totalResults})
        </Fab>
      </div>
    </>
  );
};

export default ReplacementUnits;
